import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { X, ShoppingCart, Plus, Minus, Trash2, MessageCircle, ArrowRight } from 'lucide-react'
import { useCart } from '../../context/CartContext'
import { buildWhatsAppCheckoutUrl } from '../../utils/whatsapp'

const CartDrawer = () => {
    const {
        cart,
        isOpen,
        closeCart,
        increment,
        decrement,
        removeFromCart,
        clearCart,
        totalItems,
    } = useCart()

    const handleCheckout = () => {
        const url = buildWhatsAppCheckoutUrl(cart)
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 bg-black/50 z-[60]"
                        onClick={closeCart}
                    />

                    {/* Drawer */}
                    <motion.aside
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-[70] flex flex-col"
                        role="dialog"
                        aria-label="Panier"
                    >
                        {/* Header */}
                        <div className="bg-primary-green text-white p-6 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-primary-yellow/20 rounded-full flex items-center justify-center mr-3">
                                    <ShoppingCart className="w-6 h-6 text-primary-yellow" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold">Mon Panier</h2>
                                    <p className="text-xs text-white/80">
                                        {totalItems} article{totalItems > 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closeCart}
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                                aria-label="Fermer le panier"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto">
                            {cart.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                                    <div className="w-24 h-24 bg-primary-green/10 rounded-full flex items-center justify-center mb-6">
                                        <ShoppingCart className="w-12 h-12 text-primary-green/60" />
                                    </div>
                                    <h3 className="text-xl font-bold text-primary-green mb-2">
                                        Votre panier est vide
                                    </h3>
                                    <p className="text-gray-500 mb-6">
                                        Ajoutez des produits pour passer commande
                                    </p>
                                    <Link
                                        to="/products"
                                        onClick={closeCart}
                                        className="btn-primary group"
                                    >
                                        Voir les produits
                                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                </div>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {cart.map((item) => (
                                        <li key={item.id} className="p-4 flex gap-4">
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-primary-green truncate">
                                                    {item.name}
                                                </h3>
                                                {item.categoryName && (
                                                    <p className="text-xs text-gray-500 mb-2">
                                                        {item.categoryName}
                                                    </p>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    <div className="inline-flex items-center bg-gray-100 rounded-full">
                                                        <button
                                                            onClick={() => decrement(item.id)}
                                                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-primary-green hover:text-white transition-colors"
                                                            aria-label="Diminuer"
                                                        >
                                                            <Minus className="w-4 h-4" />
                                                        </button>
                                                        <span className="w-8 text-center text-sm font-semibold">
                                                            {item.quantity}
                                                        </span>
                                                        <button
                                                            onClick={() => increment(item.id)}
                                                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-primary-green hover:text-white transition-colors"
                                                            aria-label="Augmenter"
                                                        >
                                                            <Plus className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <button
                                                        onClick={() => removeFromCart(item.id)}
                                                        className="text-red-500 hover:text-red-700 transition-colors p-2"
                                                        aria-label="Retirer du panier"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Footer */}
                        {cart.length > 0 && (
                            <div className="border-t border-gray-200 p-6 bg-gray-50 flex-shrink-0 space-y-3">
                                <div className="flex items-center justify-between text-sm text-gray-600">
                                    <span>Articles dans le panier</span>
                                    <span className="font-bold text-primary-green text-lg">
                                        {totalItems}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 leading-relaxed">
                                    Le prix total et la disponibilité vous seront confirmés sur WhatsApp.
                                </p>
                                <button
                                    onClick={handleCheckout}
                                    className="w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    <MessageCircle className="w-5 h-5 mr-2" />
                                    Finaliser sur WhatsApp
                                </button>
                                <button
                                    onClick={clearCart}
                                    className="w-full text-sm text-gray-500 hover:text-red-600 transition-colors py-2"
                                >
                                    Vider le panier
                                </button>
                            </div>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    )
}

export default CartDrawer
